import { Alert, Column, Link, Row, Text, Textarea } from '@amzn/meridian';
import { ErrorAlert } from 'components/ErrorAlert';
import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';
import { GenerateAltTextButton } from '../../components/GenerateAltTextButton';

/**
 * Layout component for DirectProps that handles hooks properly
 */
export const DirectPropsLayout = ({
  textareaProps,
  suggestion,
  onUseSuggestion,
  generateButtonProps,
  error,
  onClearError,
  showImageSizeWarning,
  imageValidationMessage,
  label,
  isDecorative,
  value,
  onChange,
  onBlur,
  hasValidationError,
}: {
  textareaProps: {
    placeholder: string;
    disabled: boolean;
    'aria-label': string;
    'data-cy'?: string;
  };
  suggestion: string;
  onUseSuggestion: () => void;
  generateButtonProps: {
    assetId?: string | null;
    locale?: string | null;
    productName: string;
    imageType: string | null;
    disableGenerate: boolean;
    onNameChange: (name: string) => void;
    onSubmit: () => void;
    onAltTextGenerated: (altText: string) => void;
    onError: (err: Error) => void;
  };
  error?: string;
  onClearError: () => void;
  showImageSizeWarning: boolean;
  imageValidationMessage?: string;
  label?: string;
  isDecorative: boolean;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  hasValidationError?: boolean;
}) => {
  const previousDecorative = usePrevious(isDecorative);

  useEffect(() => {
    if (isDecorative && isDecorative !== previousDecorative) {
      onChange?.('');
      onBlur?.();
    }
  }, [
    isDecorative,
    previousDecorative,
    onChange,
    onBlur,
  ]);

  const handleUseSuggestion = () => {
    if (suggestion) {
      onChange?.(suggestion);
      onBlur?.();
      onUseSuggestion();
    }
  };

  const handleChange = (val: string) => {
    onChange?.(val);
    onClearError();
  };

  const handleFocus = () => {
    onClearError();
  };

  return (
    <Column spacing="200">
      <Row alignmentHorizontal="justify" alignmentVertical="bottom">
        <FormLabelWithOptional label={label || 'Alt Text'} />
        {!isDecorative && generateButtonProps && <GenerateAltTextButton {...generateButtonProps} />}
      </Row>
      <Row
        alignmentVertical="center"
        widths={[
          '100%',
          'fill',
        ]}
      >
        <Textarea
          {...textareaProps}
          error={(!!error || !!hasValidationError) && !textareaProps.disabled}
          onBlur={onBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          resize="auto"
          rows={1}
          size="medium"
          value={value}
        />
      </Row>
      <Row
        alignmentVertical="center"
        widths={[
          'fill',
          'auto',
        ]}
      >
        {error && !isDecorative && <ErrorAlert contextMessage="Alt text generation failed" error={new Error(error)} />}
      </Row>
      {showImageSizeWarning && (
        <Row
          alignmentVertical="center"
          widths={[
            'fill',
            'auto',
          ]}
        >
          <Alert aria-live="polite" size="small" type="warning">
            {imageValidationMessage}
          </Alert>
        </Row>
      )}
      {!isDecorative && suggestion && (
        <Row
          alignmentVertical="center"
          widths={[
            'fill',
            'auto',
          ]}
        >
          <Text aria-live="polite" color="secondary" type="b200">
            Auto‑generated: "{suggestion}"{' '}
            <Link aria-label="Use suggested alt text" onClick={handleUseSuggestion}>
              Use
            </Link>
          </Text>
        </Row>
      )}
    </Column>
  );
};
